import React from 'react';
import { Button, ButtonVariant, Flex, FlexItem, Checkbox } from '@patternfly/react-core';
import _ from 'lodash';
/**
 * @typedef {object} CloseButtonProps
 * @prop {import('localization/useTranslation').TFunction} T
 * @prop {() => void} onClose
 * @prop {string} [closeText]
 * @prop {boolean} [isLoading]
 */

/**
 * Create close button
 * @param {CloseButtonProps} props
 * @returns {React.Component[]}
 */
export const closeButton = ({ T, onClose, closeText, isLoading }) => {
  return [
    <Button isDisabled={isLoading} isLoading={isLoading} key="close" variant={ButtonVariant.primary} onClick={onClose}>
      { closeText ?? T.common.close }
    </Button>
  ];
};
/**
 * @typedef {object} ConfirmButtonsProps
 * @prop {import('localization/useTranslation').TFunction} T
 * @prop {() => void} onConfirm
 * @prop {string} [confirmText]
 * @prop {() => void} [onCancel]
 * @prop {string} [cancelText]
 * @prop {boolean} [isLoading]
 * @prop {boolean} [isDisabled]
 */

/**
 *
 * @param {ConfirmButtonsProps} props
 * @returns {[JSX.Element, JSX.Element]}
 */
export const confirmButtons = ({ T, onConfirm, confirmText, onCancel, cancelText, isLoading, isDisabled = false }) => {
  return [
    <Button isDisabled={isDisabled || isLoading} isLoading={isLoading} key="confirm" variant={ButtonVariant.primary} onClick={onConfirm}>
      { confirmText ?? T.common.ok }
    </Button>,
    <Button isDisabled={isLoading} key="cancel" variant={ButtonVariant.link} onClick={onCancel}>
      { cancelText ?? T.common.cancel }
    </Button>
  ];
};

export const acceptButton = ({ T, onAccept, acceptText, accepted, toggle, isLoading, isDisabled = false }) => {
  return [
    <Button isDisabled={isDisabled || isLoading || !accepted} isLoading={isLoading} key="confirm" variant={ButtonVariant.primary} onClick={onAccept}>
      { T.common.close }
    </Button>,
    <Checkbox isDisabled={isLoading} key="accept" onChange={toggle} isChecked={accepted} label={acceptText}/>
  ];
};

export const saveButtons = ({ T, onSave, saveText, saveEnabled, onCancel, cancelText, isLoading }) => {
  return _.compact([
    <Button isDisabled={isLoading || !saveEnabled} isLoading={isLoading} key="save" variant={ButtonVariant.primary} onClick={onSave}>
      { saveText ?? T.common.save }
    </Button>,
    onCancel &&
      <Button isDisabled={isLoading} key="cancel" variant={ButtonVariant.link} onClick={onCancel}>
        { cancelText ?? T.common.cancel }
      </Button>
  ]);
};

export const saveButtonFooter = ({ T, onSave, saveText, saveEnabled, onDelete, deleteEnabled, deleteText, onCancel, cancelText, isLoading }) => {
  return (<Flex style={{ width: '100%' }}>
    <FlexItem><Button isDisabled={isLoading || !saveEnabled} isLoading={isLoading} key="save" variant={ButtonVariant.primary} onClick={onSave}>
      { saveText ?? T.common.save }
    </Button>
    </FlexItem>
    { onCancel && <FlexItem>
      <Button isDisabled={isLoading} key="cancel" variant={ButtonVariant.link} onClick={onCancel}>
        { cancelText ?? T.common.cancel }
      </Button></FlexItem> }
    { onDelete && <FlexItem align={{ default: 'alignRight' }}><Button isDisabled={isLoading || !deleteEnabled} isLoading={isLoading} key="delete" variant={ButtonVariant.danger} onClick={onDelete}>
      { deleteText ?? T.common.delete }
    </Button></FlexItem> }
  </Flex>);

};
/**
 * @typedef {object} DeleteButtonsProps
 * @prop {import('localization/useTranslation').TFunction} T
 * @prop {() => void} onDelete
 * @prop {string} [deleteText]
 * @prop {boolean} [deleteEnabled]
 * @prop {() => void} [onCancel]
 * @prop {string} [cancelText]
 * @prop {boolean} [isLoading]
 */

/**
 * Create delete and cancel buttons for modals
 * @param {DeleteButtonsProps} props
 * @returns {React.Component[]}
 */
export const deleteButtons = ({ T, onDelete, deleteText, deleteEnabled, onCancel, cancelText, isLoading }) => {
  return [
    <Button isDisabled={isLoading || !deleteEnabled} isLoading={isLoading} key="delete" variant={ButtonVariant.danger} onClick={onDelete}>
      { deleteText ?? T.common.delete }
    </Button>,
    ((onCancel) ?
      <Button isDisabled={isLoading} key="cancel" variant={ButtonVariant.link} onClick={onCancel}>
        { cancelText ?? T.common.cancel }
      </Button>
      : null
    )
  ];
};
